import { BottomActions, Box, Nav, PageHeader } from '@dagens/carrot';
import { useTranslation } from '@dagens/frontend-i18n';
import { Form } from '@dagens/frontend-forms';
import { Page } from '../../../../components/page';
import REQ from '../../../../utils/REQ';
import {
  CustomerDeliveryForm,
  DeliveryFormValues
} from '../../../components/customer-delivery-form';
import { ManagedCustomer } from '../../../../api';
import { useFetchCustomer } from '../shared/use-fetch-customer';
import { formatStructuredAddress } from '../../../../utils/address';
import { useMediaQuery } from '../../../../_common/hooks/useMediaQueries';
import { useOnSave } from './use-on-save';
import { DeliveryBottomActions } from './bottom-actions';

const consumerToForm = (consumer: ManagedCustomer) => {
  return {
    deliveryAddress: formatStructuredAddress(
      consumer.structuredDeliveryAddress
    ),
    deliveryInstructions: consumer.deliveryInfo ?? null,
    deliveryWindowFrom: consumer.deliveryWindow.start,
    deliveryWindowTo: consumer.deliveryWindow.end
  } satisfies DeliveryFormValues;
};

type Props = {
  consumer: ManagedCustomer;
};

const PageContent = ({ consumer }: Props) => {
  const { desktopUp } = useMediaQuery();
  const { t } = useTranslation();
  const { onSave, saveReq } = useOnSave(consumer._id);

  const title = t('producer:EditCustomerDeliveryInformation');
  const subtitle = consumer.name;

  return (
    <Form.Provider defaultValue={consumerToForm(consumer)}>
      <Page
        navMobileText={
          <>
            <Nav.ContextTitle>{title}</Nav.ContextTitle>
            <Nav.ContextSubtitle>{subtitle}</Nav.ContextSubtitle>
          </>
        }
        header={
          desktopUp && (
            <Box.FullWidth>
              <PageHeader title={title} subtitle={subtitle} />
            </Box.FullWidth>
          )
        }
        bottom={
          <BottomActions border>
            <DeliveryBottomActions
              consumerId={consumer._id}
              saveReq={saveReq}
              onSubmit={onSave}
            />
          </BottomActions>
        }
      >
        <Box.FormWrapper>
          <Form onSubmit={onSave}>
            <CustomerDeliveryForm />
          </Form>
        </Box.FormWrapper>
      </Page>
    </Form.Provider>
  );
};

export const EditCustomerDeliveryPage = () => {
  const { req, consumer } = useFetchCustomer();

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }

  if (!consumer) {
    return <Page.NotFound />;
  }

  return <PageContent consumer={consumer} />;
};
